/**
 * @fileoverview Sentence-case display transform for category pill labels
 * (FRE-1366).
 *
 * The canonical taxonomy (`CANONICAL_CATEGORY_LABELS`, COR-37) stores
 * multi-word labels in title case — "Business & Networking", "Science &
 * Tech" — for use as page titles and hub headings. The `clt.loci.city/`
 * "Browse by interest" rail renders the SAME labels in brand sentence case
 * ("Business & networking", "Science & tech") per the 2026-09-27 design:
 * only the first word keeps its capital, everything else lowercases,
 * except a word that reads as an acronym or initialism (e.g. "DJ",
 * "LGBTQ+"), which is left untouched wherever it appears.
 *
 * This is a pure **display-seam** transform. It never mutates
 * `CANONICAL_CATEGORY_LABELS`, the API payload, or any stored/queried
 * data — callers apply it only at the point text is rendered.
 *
 * @since FRE-1366
 */

/**
 * A word that reads as an acronym or initialism and must never be
 * lowercased: two or more characters, every letter uppercase, optionally
 * followed by a single trailing `+` (e.g. "DJ", "LGBTQ+"). A lone capital
 * letter ("A") does not match — sentence case still lowercases it unless
 * it is the first word.
 */
const ACRONYM_PATTERN = /^[A-Z0-9]{2,}\+?$/;

function isAcronymWord(word: string): boolean {
  return ACRONYM_PATTERN.test(word);
}

/**
 * Converts a title-case (or any-case) category label to brand sentence
 * case: the first word capitalized, every other word lowercased UNLESS it
 * is an acronym-looking word ({@link isAcronymWord}), which is left as-is
 * regardless of position. Non-alphabetic tokens (e.g. `&`) round-trip
 * unchanged since neither branch alters them.
 *
 * Pure and total — never throws; an empty string round-trips to itself.
 *
 * @example
 * toSentenceCaseCategoryLabel('Business & Networking') // 'Business & networking'
 * toSentenceCaseCategoryLabel('Science & Tech')         // 'Science & tech'
 * toSentenceCaseCategoryLabel('DJ Nights')              // 'DJ nights'
 * toSentenceCaseCategoryLabel('LGBTQ+ Meetups')         // 'LGBTQ+ meetups'
 */
export function toSentenceCaseCategoryLabel(label: string): string {
  const words = label.split(' ');
  return words
    .map((word, index) => {
      if (word.length === 0 || isAcronymWord(word)) {
        return word;
      }
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word.toLowerCase();
    })
    .join(' ');
}

/**
 * Display seam for category labels on every public surface (WEB-1189 C9).
 *
 * The canonical taxonomy stays Title Case (`CATEGORY_LABELS`). Call this at
 * the render boundary. Idempotent on an already sentence-cased label.
 */
export function categoryDisplayLabel(label: string): string {
  return toSentenceCaseCategoryLabel(label);
}
